import type { EtatDEnregistrement } from '@taki/ui';
import type { MotifDeCopie } from './poste.js';

/**
 * LES REGLES DE L'ENREGISTREMENT AUTOMATIQUE, en fonctions pures
 * (`tests/sauvegarde-regles.test.mjs`). Le moteur (`moteur.ts`) a des
 * minuteurs, des promesses et un abonnement ; ce qu'il DECIDE vit ici, une
 * regle a la fois. Chaque regle est une lecon payee par un travail perdu
 * (PR 188, puis le contre-avis de la PR 212).
 */

/** Les delais de l'enregistrement automatique, en millisecondes. */
export interface DelaisDeSauvegarde {
  /** Attente apres le dernier geste avant d'ecrire. */
  debounceMs: number;
  /** Age maximum d'un changement pas encore ecrit (cf. `doitEcrireMaintenant`). */
  attenteMaxMs: number;
  /** Delai avant la PREMIERE nouvelle tentative d'une ecriture ratee. */
  retenteMs: number;
  /** Le recul des tentatives suivantes ne depasse jamais ce delai. */
  retentePlafondMs: number;
  /** Intervalle minimum entre deux vignettes. */
  vignetteMs: number;
}

export const DELAIS_PAR_DEFAUT: Readonly<DelaisDeSauvegarde> = Object.freeze({
  debounceMs: 600,
  attenteMaxMs: 2500,
  retenteMs: 3000,
  retentePlafondMs: 60_000,
  vignetteMs: 10_000,
});

/**
 * L'AGE PASSE AVANT LE CONFORT. Un debounce seul REPOUSSE l'ecriture a chaque
 * geste : qui travaille une minute sans s'arreter n'a rien en base. Passe
 * `attenteMaxMs` depuis le PREMIER changement pas encore ecrit, on ecrit.
 */
export function doitEcrireMaintenant(premierChangement: number | null, maintenant: number, attenteMaxMs: number): boolean {
  return premierChangement !== null && maintenant - premierChangement >= attenteMaxMs;
}

/**
 * UNE REVISION VENUE DU SERVEUR APPREND-ELLE QUELQUE CHOSE ? Ni l'echo local
 * (`local`), ni une revision deja connue ou deja en vol : l'accuse de notre
 * propre ecriture arrive parfois AVANT que sa promesse se resolve, et il serait
 * pris pour le travail d'un autre. Le verrou des rules (lot 1) garantit qu'une
 * revision egale est toujours une revision deja connue.
 */
export function revisionDistanteNouvelle(connue: number, enVol: number | null, distante: number, local: boolean): boolean {
  if (local) return false;
  return distante > Math.max(connue, enVol ?? 0);
}

/** La vignette coute un rendu complet : au plus une toutes les `intervalleMs`, sauf si on la force. */
export function vignetteDue(derniere: number, maintenant: number, intervalleMs: number, forcer: boolean): boolean {
  return forcer || maintenant - derniere >= intervalleMs;
}

/**
 * CE QU'UNE ECRITURE RATEE DEMANDE. Lu par NOM et par CODE, jamais par
 * `instanceof` : l'erreur traverse deux bundles (cf. `estConflitDeVersion`,
 * `@taki/sdk`).
 *
 *  - `conflit`     : quelqu'un a enregistre avant nous. On ANNONCE, on garde
 *                    une copie du travail local, et la personne choisit ;
 *  - `refus`       : non pour de bon (archive, supprime, droits, format).
 *                    Retenter ne changera rien : on s'arrete, le travail est mis
 *                    de cote sur le poste, et on le dit ;
 *  - `trop-lourd`  : rien ne part tant que le contenu ne maigrit pas ;
 *  - `transitoire` : reseau, quota, serveur occupe : on retente, de plus en
 *                    plus espace (`delaiDeRetente`).
 */
export type IssueDEchec =
  | { issue: 'conflit'; enBase: number | null }
  | { issue: 'refus'; motif: MotifDeCopie }
  | { issue: 'trop-lourd' }
  | { issue: 'transitoire' };

const CODES_DEFINITIFS: ReadonlySet<string> = new Set(['permission-denied', 'not-found', 'invalid-argument']);

export function issueDEchec(cause: unknown): IssueDEchec {
  if (typeof cause !== 'object' || cause === null) return { issue: 'transitoire' };
  const { name, code, raison, enBase } = cause as { name?: unknown; code?: unknown; raison?: unknown; enBase?: unknown };
  if (name === 'ChargeTropLourde') return { issue: 'trop-lourd' };
  if (name === 'ConflitDeVersion') return { issue: 'conflit', enBase: typeof enBase === 'number' ? enBase : null };
  if (name === 'EcritureRefusee') {
    const motif: MotifDeCopie =
      raison === 'supprime' || raison === 'archive' || raison === 'introuvable' || raison === 'format'
        ? raison
        : 'droits';
    return { issue: 'refus', motif };
  }
  // Un code brut que le SDK n'a pas nomme : definitif s'il l'est par nature.
  if (typeof code === 'string' && CODES_DEFINITIFS.has(code)) return { issue: 'refus', motif: 'droits' };
  return { issue: 'transitoire' };
}

/**
 * LE RECUL DES NOUVELLES TENTATIVES : `retenteMs`, puis le double, jamais plus
 * de `plafondMs`. `essai` compte a partir de 1 (le premier echec).
 */
export function delaiDeRetente(essai: number, retenteMs: number, plafondMs: number): number {
  const rang = Math.max(1, Math.floor(essai));
  if (rang > 30) return plafondMs;
  return Math.min(plafondMs, retenteMs * 2 ** (rang - 1));
}

/** Pourquoi le contenu lui-meme empeche d'ecrire en place (au-dela de la porte). */
export type Blocage = 'archive' | 'supprime' | 'introuvable' | 'droits' | 'format';

/** Ce qu'un etat lu (ouverture, direct) impose : rien, l'archive, ou la suppression. */
export function blocageDeLaFiche(fiche: { archivedAt: unknown; deletedAt: unknown } | null): Blocage | null {
  if (fiche === null) return 'introuvable';
  if (fiche.deletedAt != null) return 'supprime';
  if (fiche.archivedAt != null) return 'archive';
  return null;
}

/** Un blocage que le contenu peut lever sans reouverture (desarchiver, rendre les droits). */
export function blocageReversible(blocage: Blocage | null): boolean {
  return blocage === null || blocage === 'archive';
}

/**
 * LES FAITS DONT LA BANDE SE DEDUIT. La bande ne se pilote pas par transitions
 * (la PR 212 en avait une machine, et ses cas croises faisaient mentir
 * « Enregistre ») : elle se CALCULE, a chaque changement, depuis ce qui est
 * vrai a cet instant. Une seule regle de priorite, lisible d'un coup d'oeil.
 */
export interface FaitsDeLaBande {
  /** Refus definitif : supprime, introuvable, format plus recent, droits refuses par le serveur. */
  refuse: boolean;
  /** La porte n'ouvre pas l'ecriture, ou le contenu est archive. */
  lectureSeule: boolean;
  conflit: boolean;
  tropLourd: boolean;
  /** Une ecriture a echoue et une nouvelle tentative est programmee. */
  echec: boolean;
  enVol: boolean;
  /** Du travail n'est pas encore parti (ou attend derriere l'ecriture en vol). */
  modifie: boolean;
}

/**
 * LA BANDE NE MENT JAMAIS : un probleme passe avant un progres, un progres
 * avant le repos. `modifie` des le geste (sans lui, « Enregistre » restait
 * affiche sur un travail qui n'etait nulle part) ; `en-cours` meme si d'autres
 * gestes attendent derriere (ils partiront ensuite).
 */
export function etatDeLaBande(faits: FaitsDeLaBande): EtatDEnregistrement {
  if (faits.refuse) return 'refuse';
  if (faits.conflit) return 'conflit';
  if (faits.lectureSeule) return 'lecture-seule';
  if (faits.tropLourd) return 'trop-lourd';
  if (faits.echec) return 'echec';
  if (faits.enVol) return 'en-cours';
  if (faits.modifie) return 'modifie';
  return 'a-jour';
}

/**
 * LA PHRASE D'UN CONTENU QUI NE S'ECRIT PLUS EN PLACE. Elle dit ce qui s'est
 * passe ; ce qui est arrive au travail local se dit a part (la copie de
 * secours a sa propre annonce, avec ses gestes).
 */
export function phraseDeBlocage(blocage: Blocage): string {
  switch (blocage) {
    case 'archive':
      return 'Ce contenu est archivé : il se lit, il ne se modifie plus. Désarchive-le pour le modifier.';
    case 'supprime':
      return 'Ce contenu a été supprimé ailleurs.';
    case 'introuvable':
      return "Ce contenu n'existe plus, ou ton accès ne permet plus de le lire.";
    case 'droits':
      return "Ton accès ne permet plus d'enregistrer ce contenu.";
    case 'format':
      return "Ce contenu a été enregistré par une version plus récente de l'app : recharge la page pour le modifier.";
  }
}

/** La phrase d'une copie de secours, selon ce qui l'a fait mettre de cote. */
export function phraseDeCopie(motif: MotifDeCopie): string {
  switch (motif) {
    case 'conflit':
      return "Ta version, modifiée en même temps qu'une autre, est gardée sur ce poste.";
    case 'archive':
      return 'Tes modifications non enregistrées avant l’archivage sont gardées sur ce poste.';
    case 'supprime':
    case 'introuvable':
      return 'Tes modifications non enregistrées sont gardées sur ce poste.';
    case 'droits':
      return 'Tes modifications non enregistrées avant le retrait de ton accès sont gardées sur ce poste.';
    case 'format':
      return 'Tes modifications non enregistrées sont gardées sur ce poste.';
  }
}
